import { useMemo } from "react";

import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import { VenueBasedTicketBasketPurchaseRequest } from "@app-contexts/dashboard/features/box-office/data/types/basket";
import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";
import { UserBasedTicketBasketPurchaseRequest } from "@app-contexts/user/features/basket/data/types/basket";
import { ITEM_STATUS_OPTIONS } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { removeFunctions } from "@shared/utils/function-utils";

import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import BasketRepositoryFactory from "@shared/modules/basket/factory/BasketRepositoryFactory";
import { TicketBasket } from "../types";

import { PaymentProcessingError } from "../utils/errors/PaymentProcessingError";

/**
 * Wrapper around basket functions to maintain integration with PaymentManager
 */
function useBasketPaymentService(
  basketService: Partial<IPublicBasket | IVenueBasket>,
  basketType: DataProvider
) {
  async function extendBasket(
    enable = true,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) {
    return basketService.enableExtendedExpiry(enable, { shouldPersist });
  }

  async function acceptTerms(accept = true) {
    return basketService.updateAcceptedTerms(accept);
  }

  async function emptyBasket() {
    return basketService.emptyBasket();
  }

  async function refresh(options?: { no_lock?: boolean }) {
    return basketService.refresh(options);
  }

  async function save() {
    return basketService.save();
  }

  async function updatePaymentType(paymentType: number) {
    return basketService.updatePaymentType(paymentType);
  }

  async function updateBillingAddress(
    address: Exclude<Required<TicketBasket["billing_address"]>, undefined>,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) {
    return basketService.updateBillingAddress(address, {
      shouldPersist,
    });
  }

  async function updateShippingAddress(
    address: Exclude<Required<TicketBasket["shipping_address"]>, undefined>,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) {
    return basketService.updateShippingAddress(address, {
      shouldPersist,
    });
  }

  async function purchase(
    id: Exclude<TicketBasket["id"], undefined>,
    attributes:
      | UserBasedTicketBasketPurchaseRequest
      | VenueBasedTicketBasketPurchaseRequest
  ) {
    if (
      basketType === DataProvider.Public ||
      basketType === DataProvider.User
    ) {
      const repo = new UserBasedTicketBasketRepository();

      return repo.purchase(
        id,
        attributes as UserBasedTicketBasketPurchaseRequest,
        {
          showToastOnError: false, // Should be handled by PaymentManager
        }
      );
    }

    const repo = new VenueBasedTicketBasketRepository();
    return repo.purchase(
      id,
      attributes as VenueBasedTicketBasketPurchaseRequest,
      {
        showToastOnError: false, // Should be handled by PaymentManager
      }
    );
  }

  async function reset() {
    if (!basketService.basketId) {
      throw new PaymentProcessingError(
        "Cannot reset basket without valid basket ID",
        {
          metadata: {
            operation: "reset",
            basketId: basketService.basketId,
            service: "basket payment service",
          },
        }
      );
    }
    const { basketId: id, basketUuid: uuid } = basketService;

    type RepositoryResetFunction = (
      | UserBasedTicketBasketRepository
      | VenueBasedTicketBasketRepository
    )["reset"];
    type RepositoryResetFunctionParams = Exclude<
      Parameters<RepositoryResetFunction>[1],
      undefined
    >["params"];
    const requestParams: RepositoryResetFunctionParams = {};

    let repo;
    if (
      basketType === DataProvider.Public ||
      basketType === DataProvider.User
    ) {
      repo = new UserBasedTicketBasketRepository();
      requestParams.uuid = uuid;
    } else {
      repo = new VenueBasedTicketBasketRepository();
    }

    const returnResult = await repo.reset(id, {
      showToastOnError: false, // Should be handled by PaymentManager
      params: requestParams,
    });

    const { expiry_date } = returnResult;
    basketService.setStatus(ITEM_STATUS_OPTIONS.PENDING.id);
    basketService.enableExtendedExpiry(false, { shouldPersist: false });
    basketService.updateExpiryDate(expiry_date);
    return returnResult;
  }

  async function updatePurchaserInfo(purchaser: {
    name_on_ticket: string;
    email: string;
    phone_number: string;
  }): Promise<void> {
    await basketService.updatePurchaserInfo?.(purchaser);
    await basketService.save?.();
  }

  /**
   * Retrieves a basket by ID and UUID without relying on the basket service store.
   * Used for express checkout redirects where the basket isn't in the Zustand store.
   */
  async function retrieveById(
    basketId: number,
    uuid: string,
    venueId?: number
  ): Promise<TicketBasket> {
    const repo = BasketRepositoryFactory.getRepository(basketType, venueId);
    if (!repo) {
      throw new PaymentProcessingError(
        "Cannot retrieve basket: no repository for basket type",
        { metadata: { basketType, basketId } }
      );
    }
    const basket = await repo.retrieve(basketId, {
      params: { uuid, no_lock: true },
      showToastOnError: false,
    });
    return basket as TicketBasket;
  }

  /**
   * (Stripe-specific)
   * Updates the is_stripe_wallet_payment field on the basket
   */
  async function updateIsStripeWalletPayment(isStripeWalletPayment: boolean) {
    return basketService.updateIsStripeWalletPayment(isStripeWalletPayment);
  }

  /**
   * An object representing the basket's properties, without the basket service's functions.
   */
  const basket = useMemo(() => {
    return removeFunctions(basketService);
  }, [basketService]);

  return {
    basket,
    basketType,
    save,
    reset,
    refresh,
    purchase,
    emptyBasket,
    acceptTerms,
    extendBasket,
    updatePaymentType,
    updateBillingAddress,
    updateShippingAddress,
    updatePurchaserInfo,
    updateIsStripeWalletPayment,
    retrieveById,
  };
}

export default useBasketPaymentService;

export type PaymentServiceBasket = Partial<
  IBasket & IPublicBasket & Omit<IVenueBasket, "updatePaymentType">
>;
